// What each admin page is called, for the top bar and the browser tab.
//
// The top bar knew the names of a couple of dozen pages; the rest read
// "Portal", and so did the tab, so three open tabs could not be told apart.
// The names match the sidebar's labels, so a page is called the same thing
// where it is chosen and where it is read. A Space's overview takes the
// Space's name, since "Overview" alone says nothing in a tab or in ⌘K. Detail
// routes take the name of their section.

const ADMIN_TITLES = {
  '/dashboard': 'Home',
  '/messages': 'Messages',
  '/reviews-inbox': 'Reviews',
  '/time': 'Timesheets',

  // Clients
  '/clients-overview': 'Clients overview',
  '/admin': 'All clients',
  '/clients': 'All clients',
  '/onboarding': 'Onboarding',
  '/leads': 'Lead pipeline',
  '/networking': 'Networking',
  '/health': 'Client health',
  '/nudges': 'Nudges',
  '/reports': 'Client updates',
  '/communications': 'Communications',
  '/referrals': 'Referrals & reviews',
  '/surveys': 'Surveys',

  // Sales
  '/sales': 'Sales overview',
  '/proposals': 'Proposals',
  '/price-list': 'Price list',
  '/rfp-inbox': 'RFP inbox',
  '/bid-pipeline': 'Bid pipeline',
  '/bids': 'Bid records',
  '/contracts': 'Contracts',
  '/templates': 'Templates',

  // Work
  '/projects': 'Projects',
  '/project-templates': 'Project templates',
  '/requests': 'Portal requests',
  '/approvals': 'Approvals',
  '/revisions': 'Revisions',
  '/website-review': 'Website review',
  '/document-reviews': 'Document reviews',
  '/meetings': 'Meetings',
  '/schedule': 'Schedule',
  '/time-review': 'Where time went',
  '/contractors': 'Contractors',
  '/reviews': 'Contractor reviews',

  // Money
  '/money': 'Money overview',
  '/books': 'Finance hub',
  '/invoices': 'Invoices',
  '/expenses': 'Expenses',
  '/retainers': 'Retainers',
  '/profitability': 'Profitability',
  '/forecast': 'Forecast',
  '/tax-forms': 'Tax forms (1099)',

  // Library
  '/files': 'Files & assets',
  '/portfolio-media': 'Portfolio media',
  '/brand-assets': 'Brand assets',
  '/vault': 'Brand vault',
  '/brand-strategy': 'Brand strategy',
  '/content-studio': 'Content studio',
  '/social-calendar': 'Social calendar',

  // Insights
  '/insights': 'Insights overview',
  '/metrics': 'Metrics & next steps',
  '/analytics': 'Marketing channels',
  '/portal-activity': 'Portal activity',

  '/automations': 'Automations',
  '/cms': 'Website CMS',
  '/settings': 'Settings',
  '/brief': 'Project brief',
}

// Detail pages, named for what they hold.
const ADMIN_PATTERNS = [
  [/^\/proposals\/[^/]+\/studio$/, 'Proposal'],
  [/^\/bids\/[^/]+\/studio$/, 'Bid response'],
  [/^\/bids\/[^/]+$/, 'Bid'],
  [/^\/portfolio-media\/[^/]+$/, 'Portfolio media'],
  [/^\/project-sop\/[^/]+$/, 'Project SOP'],
  [/^\/reviews-inbox\/(web|doc|video)\/[^/]+$/, 'Review'],
]

// Client and contractor pages the top bar has no title of its own for.
const CLIENT_TITLES = {
  '/messages': 'Messages',
  '/meetings': 'Meeting notes',
  '/website-review': 'Website review',
  '/requests': 'Requests',
  '/analytics': 'Analytics',
  '/reviews': 'Reviews',
  '/w9': 'W-9 tax form',
}

/** The page's name, or null when we do not know it. */
export function pageTitle(pathname, { admin = true } = {}) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/'
  if (admin) {
    if (ADMIN_TITLES[path]) return ADMIN_TITLES[path]
    const hit = ADMIN_PATTERNS.find(([re]) => re.test(path))
    return hit ? hit[1] : null
  }
  return CLIENT_TITLES[path] || null
}

/**
 * Every admin page with a name, for jumping to one by typing it. The alias and
 * the page that only exists for a client's brief are left out.
 */
export function adminPages() {
  return Object.entries(ADMIN_TITLES)
    .filter(([path]) => !['/clients', '/brief'].includes(path))
    .map(([path, title]) => ({ path, title }))
}
